import { DateTime, IANAZone } from "luxon";
import { resolveCompanyWallTime } from "../../jobs-calendar/utils/companyWallTime";

const DEFAULT_TIME_ZONE = "America/Mexico_City";

export const transformEventBase = (input) => {
  if (!input) return null;

  const eventConfig = input.eventConfig || input?.eventPreferences?.eventConfig || null;

  if (!eventConfig) return null;

  const { mode } = eventConfig;

  let eventTime =
    input.eventTime ||
    input.fixedTimes?.[0] ||
    input.bufferedTimes?.[0] ||
    input?.eventPreferences?.eventTime ||
    input?.eventPreferences?.fixedTimes?.[0] ||
    input?.eventPreferences?.bufferedTimes?.[0] ||
    null;

  if (!eventTime || mode === "CONFIGURE_LATER") return eventConfig;

  const result = JSON.parse(JSON.stringify(eventConfig));

  const getTimeHHmm = (value) => {
    if (typeof value === "string") {
      const dt = DateTime.fromISO(value);
      return dt.toFormat("HH:mm");
    }
    if (typeof value === "object" && value.start) {
      return value.start;
    }
    throw new Error("Invalid eventTime format");
  };

  const buildStartDateISO = (value) => {
    if (typeof value === "string") {
      return DateTime.fromISO(value).toISO();
    }

    if (typeof value === "object" && value.date && value.start) {
      const dt = DateTime.fromISO(value.date).set({
        hour: Number(value.start.split(":")[0]),
        minute: Number(value.start.split(":")[1]),
        second: 0,
        millisecond: 0,
      });

      return dt.toISO();
    }

    throw new Error("Invalid eventTime format for SINGLE_EXECUTION");
  };

  if (mode === "SINGLE_EXECUTION") {
    result.startDate = buildStartDateISO(eventTime);
    return result;
  }

  // REPETITIVE
  if (mode === "REPETITIVE") {
    if (!result.frequency) {
      throw new Error("frequency is required for REPETITIVE mode");
    }

    result.frequency.at = getTimeHHmm(eventTime);
    return result;
  }

  throw new Error(`Unsupported mode: ${mode}`);
};

export const applyTimeZoneToEventBase = (eventBase, timeZone = DEFAULT_TIME_ZONE, exactCalendarStart?: string | null) => {
  if (!eventBase) return null;

  //const currentCompany = useAppSelector(selectCurrentCompany);
  const resolvedTimeZone = IANAZone.isValidZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
  const result = JSON.parse(JSON.stringify(eventBase));

  const parseHourMinute = (value: string) => {
    const [hour, minute] = value.split(":").map(Number);

    if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      throw new Error(`Invalid time format: ${value}`);
    }

    return { hour, minute };
  };

  const toUtcIsoFromIntendedZone = (value: string, allowExactCalendarStart = false) => {
    const parsed = DateTime.fromISO(value, { setZone: true });

    if (!parsed.isValid) {
      throw new Error(`Invalid ISO datetime: ${value}`);
    }

    if (allowExactCalendarStart && exactCalendarStart &&
        DateTime.fromISO(exactCalendarStart).toMillis() === parsed.toMillis()) {
      return parsed.toUTC().toISO();
    }

    const wallDate = new Date(parsed.year, parsed.month - 1, parsed.day,
      parsed.hour, parsed.minute, parsed.second, parsed.millisecond);
    const resolution = resolveCompanyWallTime(wallDate, resolvedTimeZone);
    if (resolution.kind === "nonexistent") {
      throw new Error(`This local time does not exist in ${resolvedTimeZone}. Choose ${resolution.suggestedTime} or another valid time.`);
    }
    if (resolution.kind === "ambiguous") {
      throw new Error(`This local time occurs twice in ${resolvedTimeZone}. Choose the exact ${resolution.choices.map(choice => choice.label).join(" or ")} from the calendar.`);
    }
    return resolution.choices[0].instant;
  };

  const toUtcHHmm = (hhmm: string, referenceDate?: string) => {
    const { hour, minute } = parseHourMinute(hhmm);

    const base = referenceDate ? DateTime.fromISO(referenceDate) : DateTime.now().setZone(resolvedTimeZone);

    if (!base.isValid) {
      throw new Error(`Invalid reference date: ${referenceDate}`);
    }

    return DateTime.fromObject(
      {
        year: base.year,
        month: base.month,
        day: base.day,
        hour,
        minute,
        second: 0,
        millisecond: 0,
      },
      { zone: resolvedTimeZone },
    )
      .toUTC()
      .toFormat("HH:mm");
  };

  if (result.startDate) {
    result.startDate = toUtcIsoFromIntendedZone(result.startDate, true) || undefined;
  }

  if (result.endDate) {
    result.endDate = toUtcIsoFromIntendedZone(result.endDate) || undefined;
  }

  if (result.frequency?.at) {
    if (result.frequency.wallTime) {
      const policy = result.frequency.wallTime;
      if (!result.startDate) {
        throw new Error("Select a start date for this company-local recurring service before creating the job.");
      }
      if (!["earlier", "later"].includes(policy.ambiguous) || !["skip", "shiftForward"].includes(policy.nonexistent)) {
        throw new Error("Choose how this recurring service handles repeated and nonexistent local hours.");
      }
      result.frequency.wallTime = { ...policy, timeZone: resolvedTimeZone };
    } else {
      // Existing series keep their UTC frequency until explicitly converted by the business.
      result.frequency.at = toUtcHHmm(result.frequency.at, result.startDate);
    }
  }

  return result;
};

// deprecated
export const attachEventMeta = (eventBase, schema, id) => {
  if (!eventBase) return null;

  return {
    ...eventBase,
    meta: {
      action: "ASSIGN_EVENT",
      webhookUrl: eventBase?.emitter?.url,
      payload: {
        schema,
        id,
      },
    },
  };
};
